import React from 'react';
import moment from 'moment-timezone';

import { Box, Chip, Stack, Tooltip, Typography } from '../../ui/core';
import { scrollY } from '../../ui/styles';
import { ago } from './RideLiveMap';
import { distanceMeters } from './driverGlide';

// The four display directives the api derives from the phase. Labelled by what
// they do to the rider's screen rather than by their field name — `PickupEta`
// on its own told you nothing about whether the rider can see one.
const DIRECTIVES = [
  { key: 'showDriverLocation', label: 'Driver on map' },
  { key: 'showPickupEta', label: 'Pick-up ETA' },
  { key: 'showDropoffEta', label: 'Drop-off ETA' },
  { key: 'showRoute', label: 'Route' },
];

const Field = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography color="text.secondary" variant="body2">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>
      {value}
    </Typography>
  </Stack>
);

/**
 * How far the rider was from their pick-up when they last reported in, and
 * how long ago that was — the "are they actually there?" check. The fix's age
 * matters: riders only report when they open the app.
 */
const riderText = (rider, pickup) => {
  if (!rider) return 'none';
  const age = ago(rider.timestamp);
  if (!pickup) return age;
  const meters = distanceMeters({ lat: rider.lat, lng: rider.lon }, { lat: pickup.lat, lng: pickup.lon });
  const away =
    meters < 1000 ? `${Math.max(10, Math.round(meters / 10) * 10)} m` : `${(meters / 1609.34).toFixed(1)} mi`;
  return `${away} from pick-up · ${age}`;
};

const etaText = (eta) => (eta ? `${eta.minutesAway}m → ${moment(eta.estimatedArrival).format('h:mm:ss A')}` : '—');

/**
 * What this ride's rider is being shown right now, straight off the live
 * stream — the same state their app receives.
 *
 * Server state, not a re-render of it: phase, the four display directives,
 * ETAs, whether a route polyline exists, and the driver's last fix. That makes
 * it the place to check a live-ride change before it ships.
 */
const RideLiveState = ({ state, rider = null, feed = [], connected }) => (
  <Stack spacing={1}>
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="subtitle1">Live state</Typography>
      <Chip
        size="small"
        variant="outlined"
        color={connected ? 'success' : 'default'}
        label={connected ? 'Streaming' : 'Connecting'}
      />
    </Stack>

    {state ? (
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={state.phase} />
          <Typography color="text.secondary" variant="body2">
            ride {state.rideStatus} · trip {state.tripStatus}
          </Typography>
        </Stack>
        <Typography color="text.secondary" variant="caption">
          What the rider's app is told to show
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {DIRECTIVES.map(({ key, label }) => (
            <Tooltip key={key} title={`${key}: ${state[key] ? 'true' : 'false'}`}>
              <Chip
                size="small"
                variant={state[key] ? 'filled' : 'outlined'}
                color={state[key] ? 'primary' : 'default'}
                label={label}
              />
            </Tooltip>
          ))}
        </Stack>
        <Field label="pickupEta" value={etaText(state.pickupEta)} />
        <Field label="dropoffEta" value={etaText(state.dropoffEta)} />
        <Field label="routePolyline" value={state.routePolyline ? `${state.routePolyline.length} chars` : 'absent'} />
        <Field
          label="driverLocation"
          value={
            state.driverLocation
              ? `${state.driverLocation.lat.toFixed(5)}, ${state.driverLocation.lon.toFixed(5)} @ ${Math.round(state.driverLocation.bearing ?? 0)}°`
              : 'absent'
          }
        />
        <Field label="updatedAt" value={state.updatedAt ? moment(state.updatedAt).format('h:mm:ss A') : '—'} />
        {/* Ops-only, and only until pick-up: the server stops sending it once
            the ride is en route or completed. */}
        <Field label="riderLocation" value={riderText(rider, state.pickup)} />

        {/* The stream as it arrives, newest first. Bounded in the hook, and
            bounded in height here so it cannot push the event log off the
            bottom of the panel. */}
        {feed.length > 0 && (
          <Box sx={{ ...scrollY, maxHeight: 160, mt: 0.5 }}>
            {feed.map((entry) => (
              <Stack
                key={`${entry.at}-${entry.changes.join()}`}
                direction="row"
                spacing={1}
                sx={{ fontFamily: 'monospace', fontSize: 11, py: '2px' }}
              >
                <Typography component="span" variant="inherit" color="text.secondary">
                  {moment(entry.at).format('h:mm:ss A')}
                </Typography>
                <Typography component="span" variant="inherit" sx={{ minWidth: 0 }}>
                  {entry.changes.join(' · ')}
                </Typography>
              </Stack>
            ))}
          </Box>
        )}
      </Stack>
    ) : (
      <Typography color="text.secondary" variant="body2">
        {/* The api builds this state when the rider's app connects, so a ride
            nobody has opened has none to show. */}
        Nothing live for this ride.
      </Typography>
    )}
  </Stack>
);

export default RideLiveState;
